import { useState, useEffect } from "react";
import { useForm, UseFormReturn, FieldErrors } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Combobox } from "@/components/ui/combobox";
import { toast } from "@/components/ui/use-toast";
import {
  TaskTemplate,
  useTriggerTemplate,
  useListTemplates
} from "@/lib/api/checklist/templates";
import { getUserDbId } from "@/lib/models/auth";
import { useStytchMember } from "@stytch/react/b2b";
import { NewCustomerForm } from "./NewCustomerForm";
import { NewNonCustomerForm } from "./NewNonCustomerForm";
import { NewDestinationForm } from "./NewDestinationForm";
import {
  TRIGGER_TYPES,
  TriggerType,
  FormMetaDataSchema,
  FormData,
  NewCustomerFormData,
  NewNonCustomerFormData,
  NewDestinationFormData
} from "./schemas";
import parsePhoneNumber from 'libphonenumber-js';

interface TriggerTemplateModalProps {
  open: boolean;
  onClose: () => void;
  template?: TaskTemplate | null;
  isPreview?: boolean;
  previewData?: FormData;
}

export function TriggerTemplateModal({
  open,
  onClose,
  template,
  isPreview = false,
  previewData,
}: TriggerTemplateModalProps) {
  const { member } = useStytchMember();
  const userId = getUserDbId(member);
  const [selectedTriggerType, setSelectedTriggerType] = useState<TriggerType>(
    (isPreview && previewData)
      ? previewData.trigger_type
      : deriveTriggerTypeFromTemplate(template)
  );

  const listTemplatesResult = useListTemplates({ active: true, limit: 1000 });
  const templates = listTemplatesResult?.data?.data || [];

  const ACH_DEFAULT_VALUES = {
    bank_institution_name: "",
    banker_name: "",
    banker_phone: null,
    banker_email: null,
    has_lienholder: false,
    ach_form_country_type: "USA" as const,
  };

  const getDefaultValues = (triggerType?: TriggerType): Partial<FormData> => {
    if (isPreview && previewData) {
      return previewData;
    }

    const currentTriggerType = triggerType || selectedTriggerType;
    const baseDefaults = {
      trigger_type: currentTriggerType,
      template_id: template?.id || "",
      customer_name: "",
      customer_address: "",
      customer_contact_info: "",
      corn_cfr_check: false,
      canola_cfr_check: false,
      number_of_partners: 0,
      mailing_address: "",
      notes: "",
    };


    const DEFAULT_NEW_CUSTOMER_FORM_DATA: NewCustomerFormData = {
        ...baseDefaults,
        ...ACH_DEFAULT_VALUES,
        number_of_acres: 0,
        partner_names: [],
        shirt_sizes: [],
        bol_sequence: "",
        trader_id: null,
        trader_name: null,
        email: null,
        phone_number: null,
        schema_version: "v2",
        schema_type: "new_customer_form"
    } as NewCustomerFormData;

    if (currentTriggerType === TRIGGER_TYPES.NEW_CUSTOMER) {
      return DEFAULT_NEW_CUSTOMER_FORM_DATA as NewCustomerFormData;
    } else if (currentTriggerType === TRIGGER_TYPES.NEW_NON_CUSTOMER) {
      return {
        ...baseDefaults,
        ...ACH_DEFAULT_VALUES,
        number_of_bols_needed: 0,
        email: "",
        schema_version: "v2",
        schema_type: "new_non_customer_form"
      } as NewNonCustomerFormData;
    } else if (currentTriggerType === TRIGGER_TYPES.NEW_DESTINATION) {
      return {
        ...baseDefaults,
        email: null,
        phone_number: null,
        schema_version: "v1",
        schema_type: "new_destination"
      } as NewDestinationFormData;
    } else {
      return DEFAULT_NEW_CUSTOMER_FORM_DATA;
    }
  };

  const form = useForm<FormData>({
    resolver: zodResolver(FormMetaDataSchema),
    defaultValues: getDefaultValues(),
  });

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch,
  } = form;

  const onTriggerTypeChange = (value: string) => {
    const newTriggerType = value as TriggerType;
    const newDefaults = getDefaultValues(newTriggerType);
    reset(newDefaults);
    setSelectedTriggerType(newTriggerType);
  };

  const selectedTemplateId = watch("template_id");
  const triggerTemplateMutation = useTriggerTemplate();

  useEffect(() => {
    if (open && templates.length > 0) {
      console.warn("called use effect for trigger template change")
      if (!!template?.id) {
        setValue("template_id", template.id);
      } else {
        const defaultTemplate = templates.find((t: TaskTemplate) => {
          return /new client onboarding/im.test(t.name || "");
        });

        if (defaultTemplate) {
          setValue("template_id", defaultTemplate.id);
        }
      }
    }
  }, [open, templates, template, setValue]);

  // Check if the selected template is the "new client" template
  const isNewClientTemplate = (): boolean => {
    if (!selectedTemplateId) return false;
    const selectedTemplate = templates.find((t: TaskTemplate) => t.id === selectedTemplateId);
    return selectedTemplate ? /new client onboarding/im.test(selectedTemplate.name || "") : false;
  };

  const onSubmit = async (data: FormData) => {
    try {
      // Validate required fields for new client template
      if (isNewClientTemplate() && data.trigger_type === TRIGGER_TYPES.NEW_CUSTOMER) {
        const customerData = data as NewCustomerFormData;
        
        // Validate customer_name
        if (!customerData.customer_name?.trim()) {
          form.setError("customer_name", {
            type: "manual",
            message: "Customer name is required for new client template"
          });
          return;
        }

        // Validate email
        if (!customerData.email?.trim()) {
          form.setError("email", {
            type: "manual",
            message: "Email is required for new client template"
          });
          return;
        }
      }

      // Convert phone number to E164 format if it exists (for new customer and destination forms)
      let processedData = { ...data };
      if (data.trigger_type === TRIGGER_TYPES.NEW_CUSTOMER || data.trigger_type === TRIGGER_TYPES.NEW_DESTINATION) {
        const formData = data as NewCustomerFormData | NewDestinationFormData;
        if (formData.phone_number && typeof formData.phone_number === 'string') {
          const parsedNumber = parsePhoneNumber(formData.phone_number, 'US');
          if (parsedNumber?.isValid()) {
            if (data.trigger_type === TRIGGER_TYPES.NEW_CUSTOMER) {
              (processedData as NewCustomerFormData).phone_number = parsedNumber.format('E.164');
            } else {
              (processedData as NewDestinationFormData).phone_number = parsedNumber.format('E.164');
            }
          }
        }

        // Convert banker phone number to E164 format if it exists (for new customer form only)
        if (data.trigger_type === TRIGGER_TYPES.NEW_CUSTOMER) {
          const customerData = formData as NewCustomerFormData;
          if (customerData.banker_phone && typeof customerData.banker_phone === 'string') {
            const parsedBankerNumber = parsePhoneNumber(customerData.banker_phone, 'US');
            if (parsedBankerNumber?.isValid()) {
              (processedData as NewCustomerFormData).banker_phone = parsedBankerNumber.format('E.164');
            }
          }
        }
      }

      // Convert banker phone number to E164 format if it exists (for new non-customer form)
      if (data.trigger_type === TRIGGER_TYPES.NEW_NON_CUSTOMER) {
        const nonCustomerData = data as NewNonCustomerFormData;
        if (nonCustomerData.banker_phone && typeof nonCustomerData.banker_phone === 'string') {
          const parsedBankerNumber = parsePhoneNumber(nonCustomerData.banker_phone, 'US');
          if (parsedBankerNumber?.isValid()) {
            (processedData as NewNonCustomerFormData).banker_phone = parsedBankerNumber.format('E.164');
          }
        }
      }

      const triggerData = {
        trigger_source: "manual",
        trigger_meta_data: {
          ...processedData,

          on_empty_auto_assign_action: "current_user",
          triggered_by_user_id: userId,

          // trigger_type: data.trigger_type,

          // customer_name: data.customer_name,
          // customer_address: data.customer_address,
          // customer_contact_info: data.customer_contact_info,
        },
      };

      const result = await triggerTemplateMutation.mutateAsync({
        templateId: data.template_id,
        data: triggerData,
      });

      if (!result.success) throw result;

      toast({
        title: "Template triggered successfully",
        description: "Tasks have been created from the template.",
      });

      reset();
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to trigger template.",
        variant: "destructive",
      });
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const templateOptions = templates.map((t: TaskTemplate) => ({
    value: t.id,
    label: t.name,
  }));

  const triggerTypeOptions = [
    { value: TRIGGER_TYPES.NEW_CUSTOMER, label: "New Client Form" },
    { value: TRIGGER_TYPES.NEW_NON_CUSTOMER, label: "Non Client Form" },
    { value: TRIGGER_TYPES.NEW_DESTINATION, label: "New Destination Form" },
  ];

  return (
    <Dialog open={open} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="w-full max-w-[95vw] w-[60vw] md:w-[95vw] lg:w-[85vw] max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isPreview ? "Template Preview" : "Trigger Template"}</DialogTitle>
          <DialogDescription>
            {isPreview
              ? "Preview of the saved template data."
              : "Fill out the form below to trigger a template and create tasks."
            }
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-2">
          {!isPreview && (
            <span className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="space-y-2 grid">
                <Label htmlFor="trigger_type">Form Type *</Label>
                <Combobox
                  options={triggerTypeOptions}
                  value={selectedTriggerType}
                  onValueChange={onTriggerTypeChange}
                  placeholder="Select form type..."
                  className="w-full md:min-w-[400px]"
                  popoverClassName="w-full md:min-w-[400px]"
                  dwimOnChange
                  isModal
                  disabled={isPreview}
                />
                {errors.trigger_type && (
                  <p className="text-sm text-destructive">{errors.trigger_type.message}</p>
                )}
              </div>

              <div className="space-y-2 grid">
                <Label htmlFor="template">Template to Trigger *</Label>
                <Combobox
                  options={templateOptions}
                  value={selectedTemplateId}
                  onValueChange={(value) => setValue("template_id", value)}
                  placeholder="Select a template..."
                  className="w-full md:min-w-[400px]"
                  popoverClassName="w-full md:min-w-[400px]"
                  dwimOnChange
                  isModal
                  disabled={isPreview}
                />
                {errors.template_id && (
                  <p className="text-sm text-destructive">{errors.template_id.message}</p>
                )}
              </div>
            </span>
          )}

          {selectedTriggerType === TRIGGER_TYPES.NEW_CUSTOMER && (
            <NewCustomerForm
              form={form as UseFormReturn<NewCustomerFormData>}
              errors={errors as FieldErrors<NewCustomerFormData>}
              isPreview={isPreview}
              isEmailRequired={isNewClientTemplate()}
            />
          )}

          {selectedTriggerType === TRIGGER_TYPES.NEW_NON_CUSTOMER && (
            <NewNonCustomerForm
              form={form as UseFormReturn<NewNonCustomerFormData>}
              errors={errors as FieldErrors<NewNonCustomerFormData>}
              isPreview={isPreview}
            />
          )}

          {selectedTriggerType === TRIGGER_TYPES.NEW_DESTINATION && (
            <NewDestinationForm
              form={form as UseFormReturn<NewDestinationFormData>}
              errors={errors as FieldErrors<NewDestinationFormData>}
              isPreview={isPreview}
            />
          )}

          {!isPreview && (
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Triggering..." : "Trigger Template"}
              </Button>
            </DialogFooter>
          )}

          {isPreview && (
            <DialogFooter>
              <Button type="button" onClick={handleClose}>
                Close
              </Button>
            </DialogFooter>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}

function deriveTriggerTypeFromTemplate(template?: TaskTemplate | null): TriggerType {
  if (!template) {
    return TRIGGER_TYPES.NEW_CUSTOMER;
  }

  if (/new destination set up/im.test(template.name || "")) {
    return TRIGGER_TYPES.NEW_DESTINATION;
  }

  if (/non client/im.test(template.name || "")) {
    return TRIGGER_TYPES.NEW_NON_CUSTOMER;
  }

  if (/new client/im.test(template.name || "")) {
    return TRIGGER_TYPES.NEW_CUSTOMER;
  }

  return TRIGGER_TYPES.NEW_CUSTOMER;
}
